import React from 'react';
import { useTranslation } from 'react-i18next';
import CharacterVote from './CharacterVote';
import { createPlaceholderUrl } from '../../../utils/PlaceholderCreator';
import { API_BASE_URL } from '../../../constants';

const CharacterCardsDisplay = ({ character, selectedCard }) => {
    const { t } = useTranslation();
    const sanitizeFilename = (name) => {
        if (typeof name !== 'string') return '';
        return name.replace(/[\\/*?:"<>|]/g, '');
    };

    // Función para construir la URL de la imagen del traje
    const getCardImageUrl = (card) => {
        const placeholderUrl = createPlaceholderUrl(100, 100, card?.title_en?.substring(0, 2) || '??');

        if (!character || !card) return placeholderUrl;

        try {
            const characterName = sanitizeFilename(character.name_en).replace(/ /g, '_');
            const reference = sanitizeFilename(card.reference).replace(/ /g, '_');
            const costumeName = sanitizeFilename(card.title_en).replace(/ /g, '_');

            const imageName = `${characterName}_${reference}_${costumeName}.png`;

            // Use API for dynamic resizing. Width 128 is mapped in whitelist.
            return `${API_BASE_URL}/api/thumb?img=/images/character/${imageName}&width=128`;
        } catch (error) {
            console.error("Could not construct thumbnail URL for:", card.title_en, error);
            return placeholderUrl;
        }
    };

    if (!selectedCard) {
        return (
            <div className="bg-card-white rounded-2xl shadow-xl p-6">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">{t('CharacterCardsDisplay.title')}</h2>
                <p className="text-gray-500">{t('CharacterCardsDisplay.empty_msg')}</p>
            </div>
        );
    }

    return (
        <div className="bg-card-white rounded-2xl shadow-xl p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
                {t('CharacterCardsDisplay.title')}
            </h2>

            <div className="flex gap-6 items-start">
                {/* Thumbnail */}
                <div className="flex-shrink-0">
                    <img
                        src={getCardImageUrl(selectedCard)}
                        alt={selectedCard.title_en}
                        className="w-24 h-24 rounded-full object-cover object-top shadow-md border-2 border-gray-100"
                        onError={(e) => {
                            e.target.src = createPlaceholderUrl(100, 100, selectedCard.title_en?.substring(0, 2) || '??');
                        }}
                    />
                </div>

                {/* Detalles */}
                <div className="flex-1 space-y-3">
                    <div>
                        <h3 className="font-bold text-xl text-gray-800">{selectedCard.title_en}</h3>
                        {selectedCard.title_ja && (
                            <p className="text-sm text-gray-500">{selectedCard.title_ja}</p>
                        )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div className="bg-gray-50 p-3 rounded-lg">
                            <span className="text-gray-500 block text-xs uppercase tracking-wider">{t('CharacterCardsDisplay.label_reference')}</span>
                            <span className="font-semibold text-gray-800">{selectedCard.reference}</span>
                        </div>

                        <div className="bg-gray-50 p-3 rounded-lg">
                            <span className="text-gray-500 block text-xs uppercase tracking-wider">{t('CharacterCardsDisplay.label_rarity')}</span>
                            <span className="font-bold text-selected-yellow text-lg">
                                {'★'.repeat(selectedCard.stars_initial || 0)}
                            </span>
                        </div>

                        {selectedCard.release_ja && (
                            <div className="bg-gray-50 p-3 rounded-lg">
                                <span className="text-gray-500 block text-xs uppercase tracking-wider">{t('CharacterCardsDisplay.label_release_jp')}</span>
                                <span className="font-medium text-gray-800">
                                    {new Date(selectedCard.release_ja).toLocaleDateString(undefined, { timeZone: 'UTC' })}
                                </span>
                            </div>
                        )}

                        {selectedCard.release_en && (
                            <div className="bg-gray-50 p-3 rounded-lg">
                                <span className="text-gray-500 block text-xs uppercase tracking-wider">{t('CharacterCardsDisplay.label_release_en')}</span>
                                <span className="font-medium text-gray-800">
                                    {new Date(selectedCard.release_en).toLocaleDateString(undefined, { timeZone: 'UTC' })}
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Voting Component */}
            <CharacterVote characterId={character?.Id_Personaje} />
        </div>
    );
};

export default CharacterCardsDisplay;
// Revisado: 2025-12-18 [I18n Scan]
